import * as focusTrap from 'focus-trap';
import { repaint } from '../../../js/utils/node';
import { hide as hideOverlay, show as showOverlay } from '../../../js/utils/overlay';
import transitionEnd from '../../../js/utils/transition-end';

class Openable {
  constructor(element, useOverlay = true) {
    this.isActive = false;
    this.useOverlay = useOverlay;

    if (typeof element === 'string' || element instanceof String) {
      this.container = document.querySelector(element);
    } else {
      this.container = element;
    }

    this.container.hidden = true;
    this.setState(false);

    this.focusTrap = focusTrap.createFocusTrap(this.container, {
      allowOutsideClick: true,
    });

    this.clickEvent = this.clickEvent.bind(this);
    this.keydownEvent = this.keydownEvent.bind(this);
    this.addEventListeners = this.addEventListeners.bind(this);
    this.on = this.on.bind(this);
  }

  addEventListeners() {
    document.addEventListener('touchstart', this.clickEvent);
    document.addEventListener('click', this.clickEvent);
    document.addEventListener('keydown', this.keydownEvent);
    this.focusTrap.activate();
  }

  removeEventListeners() {
    document.removeEventListener('touchstart', this.clickEvent);
    document.removeEventListener('click', this.clickEvent);
    document.removeEventListener('keydown', this.keydownEvent);
    this.focusTrap.deactivate();
  }

  clickEvent(event) {
    if (!this.container.contains(event.target)) {
      event.stopPropagation();

      this.close();
    }
  }

  keydownEvent(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  setState(state) {
    this.isActive = state;
    this.container.setAttribute('aria-hidden', state ? 'false' : 'true');
  }

  toggle() {
    if (this.isActive) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    document.documentElement.style.overflow = 'hidden';

    this.setState(true);
    this.container.hidden = false;
    repaint(this.container);

    this.container.classList.add(this.classes.active);

    if (this.useOverlay) {
      showOverlay();
    }

    setTimeout(() => {
      this.addEventListeners();
    }, 0);

    if (this.onOpen) {
      this.onOpen();
    }
  }

  close() {
    document.documentElement.style.overflow = '';

    this.setState(false);

    this.container.classList.remove(this.classes.active);

    if (this.useOverlay) {
      hideOverlay();
    }

    this.removeEventListeners();

    transitionEnd(this.container, () => {
      if (!this.isActive) {
        this.container.hidden = true;

        if (this.onClosed) {
          this.onClosed();
        }
      }
    }, { once: true });

    if (this.onClose) {
      this.onClose();
    }
  }

  on(event, func) {
    if (event === 'close') {
      this.onClose = func;
    }

    if (event === 'closed') {
      this.onClosed = func;
    }

    if (event === 'open') {
      this.onOpen = func;
    }
  }
}

export default Openable;
